import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Sparkles, History, Pause, Play } from "lucide-react";
import { toast } from "sonner";

const fmt = (iso) => `${iso.slice(8, 10)}/${iso.slice(5, 7)}/${iso.slice(0, 4)}`;
const MAX_SCROLL = 8;

// Iter 127: novità degli ultimi aggiornamenti che scorrono come i titoli di coda; cronologia completa nel dialog
export default function NovitaCredits() {
  const [items, setItems] = useState([]);
  const [canManage, setCanManage] = useState(false);
  const [open, setOpen] = useState(false);
  const [paused, setPaused] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/changelog");
      setItems(data.items || []);
      setCanManage(!!data.can_manage);
    } catch (_) { /* silenzioso */ }
  };
  useEffect(() => { load(); }, []);

  const visible = useMemo(() => items.filter((i) => i.visible).slice(0, MAX_SCROLL), [items]);
  const duration = Math.max(20, visible.length * 7);

  const toggle = async (it) => {
    try {
      await api.put("/changelog/visibility", { id: it.id, visible: !it.visible });
      setItems((arr) => arr.map((x) => (x.id === it.id ? { ...x, visible: !x.visible } : x)));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Salvataggio non riuscito");
    }
  };

  if (!visible.length && !canManage) return null;

  return (
    <div className="md:w-[320px] md:shrink-0 rounded-xl bg-white/5 border border-white/10 overflow-hidden flex flex-col" data-testid="home-novita">
      <div className="flex items-center justify-between px-3 py-2 border-b border-white/10">
        <div className="text-[10px] uppercase tracking-widest text-[#8ab0ff] font-bold flex items-center gap-1.5"><Sparkles className="h-3 w-3" /> Novità dell'app</div>
        <div className="flex items-center gap-1">
          <button type="button" onClick={() => setPaused((p) => !p)} className="text-slate-400 hover:text-white p-1" title={paused ? "Riprendi" : "Pausa"} data-testid="home-novita-pause">
            {paused ? <Play className="h-3 w-3" /> : <Pause className="h-3 w-3" />}
          </button>
          <button type="button" onClick={() => setOpen(true)} className="text-[11px] font-bold text-slate-300 hover:text-white inline-flex items-center gap-1" data-testid="home-novita-storia">
            <History className="h-3 w-3" /> Cronologia
          </button>
        </div>
      </div>
      <div className="relative h-[210px] overflow-hidden novita-fade" onMouseEnter={() => setPaused(true)} onMouseLeave={() => setPaused(false)}>
        {visible.length === 0 ? (
          <div className="p-3 text-xs text-slate-400">Nessuna novità visibile: scegli cosa mostrare dalla cronologia.</div>
        ) : (
          <div className="novita-credits px-3" style={{ animationDuration: `${duration}s`, animationPlayState: paused ? "paused" : "running" }} data-testid="home-novita-scroll">
            {visible.map((it) => (
              <div key={it.id} className="py-3 border-b border-white/5" data-testid={`home-novita-item-${it.id}`}>
                <div className="text-[10px] text-slate-400 font-mono">v{it.version} · {fmt(it.date)}</div>
                <div className="font-heading font-bold text-sm text-white leading-tight">{it.title}</div>
                <div className="text-xs text-slate-300 mt-0.5 leading-snug">{it.desc}</div>
              </div>
            ))}
          </div>
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="novita-dialog">
          <DialogHeader><DialogTitle className="font-heading flex items-center gap-2"><History className="h-5 w-5 text-[#0044FF]" /> Cronologia aggiornamenti</DialogTitle></DialogHeader>
          {canManage && <p className="text-xs text-slate-500">Spunta gli aggiornamenti da lasciare visibili nella Home di tutti gli utenti (nel riquadro scorrono gli ultimi {MAX_SCROLL} visibili).</p>}
          <ul className="divide-y">
            {items.map((it) => (
              <li key={it.id} className={`py-3 flex gap-3 ${it.visible ? "" : "opacity-50"}`} data-testid={`novita-row-${it.id}`}>
                {canManage && <input type="checkbox" className="mt-1 h-4 w-4" checked={it.visible} onChange={() => toggle(it)} data-testid={`novita-toggle-${it.id}`} />}
                <div className="min-w-0">
                  <div className="text-[11px] text-slate-500 font-mono">v{it.version} · {fmt(it.date)} · {it.per}</div>
                  <div className="font-heading font-bold text-sm">{it.title}</div>
                  <div className="text-sm text-slate-600">{it.desc}</div>
                </div>
              </li>
            ))}
          </ul>
        </DialogContent>
      </Dialog>
    </div>
  );
}
